import React, { useState, useEffect, useRef } from 'react';
import {
    FaUser, FaSearch, FaFilter, FaHeart, FaStar, FaEye,
    FaMapMarkerAlt, FaGraduationCap, FaBriefcase,
    FaCheckCircle, FaSpinner, FaChevronDown, FaChevronUp,
    FaThLarge, FaList, FaUsers, FaPhone, FaLock, FaRocket,
    FaSlidersH, FaTimes, FaPlus, FaMoneyBillWave, FaUtensils,
    FaGlobe, FaTransgender, FaCalendarAlt, FaCheck
} from 'react-icons/fa';
import {
    getBiodataMatches,
    addToShortlist,
    sendInterest,
    getShortlistedUsers,
    getInterests,
    getUserActivePlans,
    getBoostedProfiles,
    User_StateGet,
    getDistrictsByState,
    getCitiesByDistrict,
    getReligionOptions,
    User_CasteGet,
} from '../../Api/CoreApi';
import { BASE_API } from "../../Api/Api";
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { handleShortlistUser, fetchShortlistedUsers, addShortlistListener } from '../../utils/shortlistUtils';
import './AllUsers.css'; // ✅ Imported External CSS

// ✅ Food lifestyle backend → display label map
const FOOD_BACKEND_TO_DISPLAY = {
    'vegetarian': 'Vegetarian',
    'non_vegetarian': 'Non-Vegetarian',
    'eggetarian': 'Eggetarian',
    'vegan': 'Vegan',
    'jain': 'Jain',
};

const FOOD_DISPLAY_TO_BACKEND = {
    'Vegetarian': 'vegetarian',
    'Non-Vegetarian': 'non_vegetarian',
    'Eggetarian': 'eggetarian',
    'Vegan': 'vegan',
    'Jain': 'jain',
};

const getFoodDisplayLabel = (user) => {
    if (user.food_lifestyle_display) return user.food_lifestyle_display;
    if (user.food_lifestyle) {
        const lower = user.food_lifestyle.trim().toLowerCase();
        return FOOD_BACKEND_TO_DISPLAY[lower] || user.food_lifestyle;
    }
    if (user.diet) {
        const lower = user.diet.trim().toLowerCase();
        return FOOD_BACKEND_TO_DISPLAY[lower] || user.diet;
    }
    return '';
};

// ====================================================
// 🚀 NEW COMPONENT: Searchable & Creatable Dropdown
// ====================================================
const SearchableMultiSelectDropdown = ({ label, icon, options, selected, onChange, onClear, placeholder, loading = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Filter matching options
    const filteredOptions = options.filter(opt =>
        opt.toLowerCase().includes(inputValue.toLowerCase())
    );

    // Exact match check for showing "Add custom" button
    const isExactMatchFound = options.some(opt => opt.toLowerCase() === inputValue.trim().toLowerCase());

    const handleAddCustom = (e) => {
        if (e) e.preventDefault();
        const val = inputValue.trim();
        if (val) {
            // Check if already in selected array
            if (!selected.some(s => s.toLowerCase() === val.toLowerCase())) {
                onChange(val); // This adds it (toggle logic in parent will add it since it's not present)
            }
            setInputValue('');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddCustom();
        } else if (e.key === 'Backspace' && inputValue === '' && selected.length > 0) {
            // Optional: delete last tag on backspace
            onChange(selected[selected.length - 1]);
        }
    };

    return (
        <div ref={dropdownRef} className="au-ms-container">
            {label && (
                <label className="au-ms-label">
                    {icon} {label}
                    {loading && <FaSpinner style={{ animation: 'auSpin 1s linear infinite', marginLeft: 4 }} />}
                </label>
            )}
            <div className={`au-ms-box ${isOpen ? 'open' : ''}`} onClick={() => inputRef.current?.focus()}>
                {selected.map(val => (
                    <span key={val} className="au-ms-chip">
                        {val}
                        <span className="au-ms-chip-remove" onClick={(e) => { e.stopPropagation(); onChange(val); }}>
                            <FaTimes />
                        </span>
                    </span>
                ))}

                <input
                    ref={inputRef}
                    type="text"
                    className="au-ms-input"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={selected.length === 0 ? placeholder : ''}
                />

                {selected.length > 0 && (
                    <button onClick={(e) => { e.stopPropagation(); onClear(); }} style={{ background: 'none', border: 'none', color: '#ff0055', cursor: 'pointer', fontSize: 12, padding: '0 4px', marginLeft: 'auto' }}>
                        <FaTimes />
                    </button>
                )}
            </div>

            {isOpen && (
                <div className="au-ms-dropdown">
                    {loading ? (
                        <div style={{ padding: '12px', textAlign: 'center', color: '#ff0055', fontSize: 12 }}>
                            <FaSpinner style={{ animation: 'auSpin 1s linear infinite' }} /> Loading...
                        </div>
                    ) : (
                        <>
                            {/* Creatable custom option */}
                            {inputValue.trim() && !isExactMatchFound && (
                                <div className="au-ms-add-custom" onClick={handleAddCustom}>
                                    <FaPlus size={10} style={{ marginRight: 6 }} /> Add "{inputValue.trim()}"
                                </div>
                            )}

                            {filteredOptions.length === 0 && !inputValue.trim() ? (
                                <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: 12 }}>No options available</div>
                            ) : (
                                filteredOptions.map(opt => {
                                    const isSelected = selected.includes(opt);
                                    return (
                                        <div
                                            key={opt}
                                            className={`au-ms-option ${isSelected ? 'selected' : ''}`}
                                            onClick={(e) => { e.stopPropagation(); onChange(opt); setInputValue(''); }}
                                        >
                                            <div className={`au-ms-checkbox ${isSelected ? 'checked' : ''}`}>
                                                {isSelected && <FaCheck />}
                                            </div>
                                            {opt}
                                        </div>
                                    )
                                })
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const AllUsers = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
    const [shortlistedIds, setShortlistedIds] = useState([]);
    const [interestSentIds, setInterestSentIds] = useState([]);
    const [actionLoading, setActionLoading] = useState({});
    const [currentUserGender, setCurrentUserGender] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [genderLoaded, setGenderLoaded] = useState(false);
    const [activePlans, setActivePlans] = useState([]);
    const [viewedContacts, setViewedContacts] = useState([]);
    const [boostedIds, setBoostedIds] = useState([]);
    const [currentUserCaste, setCurrentUserCaste] = useState('');

    const [allStates, setAllStates] = useState([]);
    const [allReligions, setAllReligions] = useState([]);
    const [allCastes, setAllCastes] = useState([]);
    const [isMasterDataLoading, setIsMasterDataLoading] = useState(false);

    const [districtOptions, setDistrictOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [districtLoading, setDistrictLoading] = useState(false);
    const [cityLoading, setCityLoading] = useState(false);

    const [showLockModal, setShowLockModal] = useState(false);
    const masterDataLoaded = useRef(false);

    const navigate = useNavigate();

    const [advancedFilters, setAdvancedFilters] = useState({
        states: [],
        districts: [],
        motherTongues: [],
        foodStyles: [],
        jobs: [],
        educations: [],
        ageMin: '',
        ageMax: '',
        salaryMin: '',
        salaryMax: '',
        heightMin: '',
        heightMax: '',
        maritalStatus: 'all',
        smoking: 'all',
        drinking: 'all',
        showOnlyVerified: false,
        showOnlyOnline: false,
        showOnlyWithImages: false,
    });

    // ============================================
    // PLAN CHECK HELPERS
    // ============================================
    const hasActivePlan = () => isLoggedIn && activePlans.length > 0;
    const isProfileUnlocked = (userId) => viewedContacts.includes(String(userId));

    const canSendInterest = (userId) => {
        if (interestSentIds.includes(String(userId))) return true;
        if (isProfileUnlocked(String(userId))) return true;
        if (hasActivePlan()) return true;
        return false;
    };

    // ============================================
    // DISPLAY HELPERS
    // ============================================
    const getDisplayId = (user) => {
        if (!user) return 'N/A';
        if (user.profile_id) return `${user.profile_id}`;
        if (user.User_id || user.user_id || user.id) return user.User_id || user.user_id || user.id;
        return 'N/A';
    };

    const getOppositeGender = (gender) => {
        if (!gender) return '';
        if (gender === 'Male') return 'Female';
        if (gender === 'Female') return 'Male';
        return '';
    };

    const getGenderFilterInfo = () => {
        if (!isLoggedIn || !currentUserGender) return null;
        const opposite = getOppositeGender(currentUserGender);
        return { showing: opposite, icon: opposite === 'Female' ? '👩' : '👨' };
    };

    const getDisplayName = (user) =>
        user.name || user.firstname || user.fullname || user.first_name || user.username || 'Unknown';

    const getDisplayImage = (user) => {
        if (user.image && typeof user.image === 'string') return user.image;
        if (user.pic && typeof user.pic === 'string') return user.pic;
        return `https://ui-avatars.com/api/?name=${getDisplayName(user)}&background=ff0055&color=fff&size=128`;
    };

    const getDisplayLocation = (user) => user.location || user.city || user.district || user.state || 'N/A';
    const getDisplayEducation = (user) => user.course || user.education || 'N/A';
    const getDisplayJob = (user) => user.job_title || user.occupation || user.job || 'N/A';

    const canViewContact = (userId) => {
        if (viewedContacts.includes(userId)) return true;
        const contactPlans = activePlans.filter(p => p.plan_type === 'contact_view');
        const totalRemaining = contactPlans.reduce((sum, p) => {
            if (p.contact_view_limit >= 9999) return 999999;
            return sum + (p.remaining_views || 0);
        }, 0);
        return totalRemaining > 0;
    };

    const getRemainingViews = () => {
        const contactPlans = activePlans.filter(p => p.plan_type === 'contact_view');
        if (contactPlans.length === 0) return 0;
        const isUnlimited = contactPlans.some(p => p.contact_view_limit >= 9999);
        if (isUnlimited) return '∞';
        return contactPlans.reduce((sum, p) => sum + (p.remaining_views || 0), 0);
    };

    const hasBoostPlan = () => activePlans.some(p => p.plan_type === 'profile_boost');

    const getUniqueValues = (key) => {
        const values = users.map(user => user[key]).filter(Boolean);
        return [...new Set(values)];
    };

    // ============================================
    // API CALLS
    // ============================================
    const fetchUserPlans = async () => {
        try {
            const data = await getUserActivePlans();
            setActivePlans(data?.plans || []);
            setViewedContacts(data?.viewed_contacts || []);
        } catch (err) {
            setActivePlans([]);
            setViewedContacts([]);
        }
    };

    const fetchBoostedProfilesData = async () => {
        try {
            const data = await getBoostedProfiles();
            setBoostedIds(data.boosted_user_ids || []);
        } catch (err) { }
    };

    const getCurrentUserGender = async () => {
        const userId = localStorage.getItem('user_id');
        const token = localStorage.getItem('access_token');
        if (!userId || !token) {
            setIsLoggedIn(false);
            setGenderLoaded(true);
            return null;
        }
        setIsLoggedIn(true);
        try {
            const savedGender = localStorage.getItem('user_gender');
            const savedCaste = localStorage.getItem('user_caste');
            if (savedGender && savedCaste) {
                setCurrentUserGender(savedGender);
                setCurrentUserCaste(savedCaste);
                setGenderLoaded(true);
                return savedGender;
            }
            const response = await fetch(`${BASE_API}/UserData/`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const dataArray = await response.json();
                const currentUser = Array.isArray(dataArray) ? dataArray.find(u => String(u?.user || u?.user_id || u?.id || u?.User_id) === String(userId)) : null;
                if (currentUser) {
                    if (currentUser?.gender) { setCurrentUserGender(currentUser.gender); localStorage.setItem('user_gender', currentUser.gender); }
                    if (currentUser?.caste) { setCurrentUserCaste(currentUser.caste); localStorage.setItem('user_caste', currentUser.caste); }
                    setGenderLoaded(true);
                    return currentUser?.gender;
                }
            }
            setGenderLoaded(true);
            return null;
        } catch (error) {
            setGenderLoaded(true);
            return null;
        }
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const data = await getBiodataMatches();
            let allMatches = [];
            if (data.topMatch && data.topMatch.id) allMatches.push(data.topMatch);
            if (data.moreMatches && Array.isArray(data.moreMatches)) allMatches = [...allMatches, ...data.moreMatches];
            if (allMatches.length === 0 && data.profiles) allMatches = data.profiles || [];

            // Remove duplicates & empty profiles
            const seenIds = new Set();
            allMatches = allMatches.filter(m => {
                const id = m.id || m.bid || m.User_id || m.user_id;
                if (!id || seenIds.has(String(id))) return false;
                seenIds.add(String(id));
                return m.firstname || m.username || m.name || m.fullname;
            });

            setUsers(allMatches);
            setFilteredUsers(allMatches);

            // Fetch relations
            try { setShortlistedIds((await fetchShortlistedUsers(true)) || []); } catch (e) { setShortlistedIds([]); }
            try {
                const interests = await getInterests();
                const backendIds = interests.map(item => String(item.to_user || '')).filter(Boolean);
                const localIds = JSON.parse(localStorage.getItem('interest_sent_ids') || '[]');
                const mergedIds = [...new Set([...backendIds, ...localIds])];
                setInterestSentIds(mergedIds);
                localStorage.setItem('interest_sent_ids', JSON.stringify(mergedIds));
            } catch (e) { }

        } catch (err) {
            setError('Failed to load profiles');
        } finally {
            setLoading(false);
        }
    };

    // MASTER DATA LOAD
    useEffect(() => {
        if (masterDataLoaded.current) return;
        masterDataLoaded.current = true;
        const loadMasterData = async () => {
            setIsMasterDataLoading(true);
            try {
                const statesRes = await User_StateGet();
                setAllStates(Array.isArray(statesRes) ? statesRes.map(s => s?.state || s?.name || s).filter(Boolean) : []);
            } catch (err) { } finally {
                setIsMasterDataLoading(false);
            }
        };
        loadMasterData();
    }, []);

    // LOAD DISTRICTS
    const loadDistricts = async (stateName) => {
        if (!stateName) { setDistrictOptions([]); return; }
        setDistrictLoading(true);
        try {
            const res = await getDistrictsByState(stateName);
            let districts = Array.isArray(res) ? res.map(d => d?.name || d?.district || d) : [];
            if (districts.length === 0) districts = [...new Set(users.filter(u => u.state?.toLowerCase() === stateName.toLowerCase()).map(u => u.district).filter(Boolean))];
            setDistrictOptions(prev => [...new Set([...prev, ...districts])]);
        } catch (err) { } finally { setDistrictLoading(false); }
    };

    // LOAD CITIES
    const loadCities = async (districtName) => {
        if (!districtName) { setCityOptions([]); return; }
        setCityLoading(true);
        try {
            const res = await getCitiesByDistrict(districtName);
            setCityOptions(prev => [...new Set([...prev, ...(Array.isArray(res) ? res.map(c => c?.city || c?.name || c) : [])])]);
        } catch (err) { } finally { setCityLoading(false); }
    };

    // ============================================
    // HANDLE MULTI SELECT (With Custom Values Support)
    // ============================================
    const handleMultiSelect = async (key, value) => {
        const valStr = String(value).trim();
        const currentSelected = advancedFilters[key] || [];
        const isRemoving = currentSelected.some(v => v.toLowerCase() === valStr.toLowerCase());

        setAdvancedFilters(prev => {
            const current = prev[key] || [];
            if (isRemoving) {
                return { ...prev, [key]: current.filter(v => v.toLowerCase() !== valStr.toLowerCase()) };
            }
            return { ...prev, [key]: [...current, valStr] };
        });

        if (key === 'states') {
            if (!isRemoving) await loadDistricts(valStr);
            else if (currentSelected.length <= 1) {
                setDistrictOptions([]); setCityOptions([]);
                setAdvancedFilters(prev => ({ ...prev, states: [], districts: [] }));
            }
        }

        if (key === 'districts') {
            if (!isRemoving) await loadCities(valStr);
            else if (currentSelected.length <= 1) {
                setCityOptions([]);
                setAdvancedFilters(prev => ({ ...prev, districts: [] }));
            }
        }
    };

    const handleClearMultiSelect = (key) => {
        if (key === 'states') { setDistrictOptions([]); setCityOptions([]); setAdvancedFilters(prev => ({ ...prev, states: [], districts: [] })); }
        else if (key === 'districts') { setCityOptions([]); setAdvancedFilters(prev => ({ ...prev, districts: [] })); }
        else setAdvancedFilters(prev => ({ ...prev, [key]: [] }));
    };

    const handleClearAllFilters = () => {
        setAdvancedFilters({ states: [], districts: [], motherTongues: [], foodStyles: [], jobs: [], educations: [], ageMin: '', ageMax: '', salaryMin: '', salaryMax: '', heightMin: '', heightMax: '', maritalStatus: 'all', smoking: 'all', drinking: 'all', showOnlyVerified: false, showOnlyOnline: false, showOnlyWithImages: false });
        setSearchTerm(''); setDistrictOptions([]); setCityOptions([]);
    };

    // ============================================
    // FILTER EFFECT
    // ============================================
    useEffect(() => {
        let result = [...users];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase().trim();
            result = result.filter(u => Object.values(u).some(val => String(val).toLowerCase().includes(term)));
        }
        if (advancedFilters.ageMin) result = result.filter(u => parseInt(u.age) >= parseInt(advancedFilters.ageMin));
        if (advancedFilters.ageMax) result = result.filter(u => parseInt(u.age) <= parseInt(advancedFilters.ageMax));
        if (advancedFilters.salaryMin) result = result.filter(u => parseInt(u.annual_income || u.salary) >= parseInt(advancedFilters.salaryMin));
        if (advancedFilters.salaryMax) result = result.filter(u => parseInt(u.annual_income || u.salary) <= parseInt(advancedFilters.salaryMax));

        const parseHeight = (h) => {
            const str = String(h || '').toLowerCase().trim();
            if (str.includes('cm')) return parseFloat(str);
            const ft = str.match(/(\d+)\s*'\s*(\d+)?/);
            if (ft) return Math.round((parseInt(ft[1] || 0) * 12 + parseInt(ft[2] || 0)) * 2.54);
            return parseFloat(str.match(/\d+(\.\d+)?/)?.[0] || null);
        };
        if (advancedFilters.heightMin) result = result.filter(u => parseHeight(u.height) >= parseFloat(advancedFilters.heightMin));
        if (advancedFilters.heightMax) result = result.filter(u => parseHeight(u.height) <= parseFloat(advancedFilters.heightMax));

        if (advancedFilters.states.length > 0) result = result.filter(u => advancedFilters.states.some(s => u.state?.toLowerCase() === s.toLowerCase()));
        if (advancedFilters.districts.length > 0) result = result.filter(u => advancedFilters.districts.some(d => u.district?.toLowerCase() === d.toLowerCase()));
        if (advancedFilters.jobs.length > 0) result = result.filter(u => advancedFilters.jobs.some(j => (u.job_title || u.occupation || '').toLowerCase().includes(j.toLowerCase())));
        if (advancedFilters.educations.length > 0) result = result.filter(u => advancedFilters.educations.some(e => (u.course || u.education || '').toLowerCase().includes(e.toLowerCase())));
        if (advancedFilters.motherTongues.length > 0) result = result.filter(u => advancedFilters.motherTongues.some(t => u.mother_tongue?.toLowerCase() === t.toLowerCase()));

        if (advancedFilters.foodStyles.length > 0) {
            result = result.filter(u => {
                const storedVal = (u.food_lifestyle || '').trim().toLowerCase();
                const displayVal = (u.food_lifestyle_display || '').trim().toLowerCase();
                const dietVal = (u.diet || '').trim().toLowerCase();
                return advancedFilters.foodStyles.some(selected => {
                    const selLow = selected.toLowerCase();
                    const backKey = FOOD_DISPLAY_TO_BACKEND[selected] || selLow;
                    return displayVal === selLow || storedVal === backKey || dietVal === backKey || dietVal === selLow;
                });
            });
        }

        if (advancedFilters.maritalStatus !== 'all') result = result.filter(u => (u.marrige_status || u.marital_status)?.toLowerCase() === advancedFilters.maritalStatus.toLowerCase());
        if (advancedFilters.smoking !== 'all') result = result.filter(u => u.smoking?.toLowerCase() === advancedFilters.smoking.toLowerCase());
        if (advancedFilters.drinking !== 'all') result = result.filter(u => u.drinking?.toLowerCase() === advancedFilters.drinking.toLowerCase());
        if (advancedFilters.showOnlyVerified) result = result.filter(u => u.verified);
        if (advancedFilters.showOnlyOnline) result = result.filter(u => u.is_online);
        if (advancedFilters.showOnlyWithImages) result = result.filter(u => !(!u.pic || u.pic.includes('profilepic.jpg') || u.pic.includes('default')));

        result.sort((a, b) => {
            const aId = String(a.User_id || a.user_id || a.user || a.id || a.bid || '');
            const bId = String(b.User_id || b.user_id || b.user || b.id || b.bid || '');
            return (boostedIds.includes(bId) ? 1 : 0) - (boostedIds.includes(aId) ? 1 : 0);
        });

        setFilteredUsers(result);
    }, [searchTerm, advancedFilters, users, boostedIds]);

    // INITIALIZE
    useEffect(() => {
        const initialize = async () => {
            await getCurrentUserGender();
            await fetchUserPlans();
            await fetchBoostedProfilesData();
            await fetchUsers();
        };
        initialize();
        return addShortlistListener((newIds) => setShortlistedIds(newIds));
    }, []);

    const handleShortlist = async (userId) => {
        if (!isLoggedIn) return navigate('/User_Login');
        setActionLoading(prev => ({ ...prev, [userId]: 'shortlist' }));
        await handleShortlistUser(userId, setShortlistedIds);
        setActionLoading(prev => ({ ...prev, [userId]: null }));
    };

    const handleInterest = async (userId) => {
        if (!isLoggedIn) return navigate('/User_Login');
        if (interestSentIds.includes(String(userId))) return;
        if (!canSendInterest(userId)) return setShowLockModal(true);
        setActionLoading(prev => ({ ...prev, [userId]: 'interest' }));
        try {
            const response = await sendInterest(userId);
            if (response && response.id) {
                setInterestSentIds(prev => [...prev, String(userId)]);
                message.success("Interest sent!");
            }
        } catch (err) { message.info("Interest already sent or failed!"); }
        setActionLoading(prev => ({ ...prev, [userId]: null }));
    };

    const getActiveFilterCount = () => {
        return Object.entries(advancedFilters).filter(([k, v]) => Array.isArray(v) ? v.length > 0 : (v !== '' && v !== 'all' && v !== false)).length;
    };

    if (loading || !genderLoaded) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 16, background: '#fff', padding: '20px' }}>
                <FaSpinner size={40} style={{ color: '#ff0055', animation: 'auSpin 1s linear infinite' }} />
                <p style={{ color: '#888', fontSize: 13, textAlign: 'center' }}>💖 Loading matched profiles...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', background: '#fff', padding: '16px' }}>
                <div style={{ background: '#fff', border: '2px solid #ffe4ec', borderRadius: 20, padding: '30px 20px', width: '100%', maxWidth: 400, textAlign: 'center' }}>
                    <h2 style={{ color: '#ff0055', marginBottom: 12, fontSize: 20 }}>⚠️ Error</h2>
                    <p style={{ color: '#888', fontSize: 13 }}>{error}</p>
                    <button onClick={() => window.location.reload()} style={{ marginTop: 20, padding: '11px 30px', background: 'linear-gradient(135deg,#ff0055,#ff4d88)', color: '#fff', border: 'none', borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Try Again</button>
                </div>
            </div>
        );
    }

    const LockModal = () => (
        <div className="au-lock-overlay" onClick={() => setShowLockModal(false)}>
            <div className="au-lock-modal" onClick={(e) => e.stopPropagation()}>
                <button className="au-lock-close" onClick={() => setShowLockModal(false)}><FaTimes /></button>
                <div className="au-lock-icon">🔒</div>
                <h3 className="au-lock-title">Plan Required!</h3>
                <p className="au-lock-desc">You need an active plan to send interest.</p>
                <div className="au-lock-features">
                    <div className="au-lock-feature"><FaHeart style={{ color: '#ff0055' }} /><span>Unlimited interests</span></div>
                    <div className="au-lock-feature"><FaPhone style={{ color: '#ff0055' }} /><span>View contacts</span></div>
                </div>
                <button className="au-lock-btn-primary" onClick={() => navigate('/recharge')}>🚀 Buy Plan</button>
                <button className="au-lock-btn-secondary" onClick={() => setShowLockModal(false)}>Maybe Later</button>
            </div>
        </div>
    );

    return (
        <>
            {showLockModal && <LockModal />}
            <div className="au-page">
                <div className="au-wrap">
                    <div className="au-header">
                        <div className="au-title-section">
                            <h1 className="au-title"><FaUsers size={20} /> All Profiles</h1>
                            <p className="au-subtitle">
                                <strong>{filteredUsers.length}</strong> matched profiles found 💖
                                {getGenderFilterInfo() && <span style={{ marginLeft: 6, color: '#888' }}>· {getGenderFilterInfo().icon} Showing {getGenderFilterInfo().showing} profiles</span>}
                            </p>
                        </div>
                    </div>

                    <div className="au-top-actions">
                        <div className="au-search-wrap">
                            <FaSearch className="au-search-icon" />
                            <input type="text" placeholder="Search by name, location, caste, education..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="au-search" />
                        </div>

                        {/* Advanced Search button - Mobile pe bhi dikhega */}
                        <button className={`au-filter-btn ${showAdvancedSearch ? 'active' : ''}`} onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}>
                            <FaSlidersH size={11} />
                            <span className="au-filter-btn-text">Advanced Search</span>
                            {getActiveFilterCount() > 0 && <span className="filter-badge">{getActiveFilterCount()}</span>}
                            {showAdvancedSearch ? <FaChevronUp size={9} /> : <FaChevronDown size={9} />}
                        </button>

                        {/* View Toggle - Mobile pe hide */}
                        <div className="au-view-toggle au-hide-mobile">
                            <button className={`au-view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}><FaThLarge size={12} /></button>
                            <button className={`au-view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}><FaList size={12} /></button>
                        </div>
                    </div>

                    {showAdvancedSearch && (
                        <div className="au-advanced-search">
                            <div className="au-advanced-search-title">
                                <FaSlidersH /> Advanced Search
                                <span style={{ fontSize: 11, fontWeight: 400, color: '#888', marginLeft: 'auto' }}>{getActiveFilterCount()} active filters</span>
                            </div>
                            <div className="au-advanced-grid">

                                <div className="au-advanced-section"><FaMapMarkerAlt /> Location</div>
                                <SearchableMultiSelectDropdown
                                    label="States" icon="📍"
                                    options={allStates.length > 0 ? allStates : getUniqueValues('state')}
                                    selected={advancedFilters.states} onChange={(val) => handleMultiSelect('states', val)} onClear={() => handleClearMultiSelect('states')}
                                    placeholder="Type or select state..." loading={isMasterDataLoading}
                                />
                                <div>
                                    <label style={{ fontSize: 10.5, fontWeight: 700, color: '#c2185b', display: 'block', marginBottom: 4 }}>🏙️ DISTRICTS</label>
                                    <SearchableMultiSelectDropdown
                                        options={districtOptions} selected={advancedFilters.districts}
                                        onChange={(val) => handleMultiSelect('districts', val)} onClear={() => handleClearMultiSelect('districts')}
                                        placeholder={advancedFilters.states.length === 0 ? "Select state first" : "Type or select district..."} loading={districtLoading}
                                    />
                                </div>

                                <div className="au-advanced-section"><FaBriefcase /> Profession & Education</div>
                                <SearchableMultiSelectDropdown
                                    label="Jobs / Occupations" icon="💼"
                                    options={[...new Set(users.map(u => u.job_title?.trim()).filter(Boolean))]}
                                    selected={advancedFilters.jobs} onChange={(val) => handleMultiSelect('jobs', val)} onClear={() => handleClearMultiSelect('jobs')}
                                    placeholder="Type or select job..."
                                />
                                <SearchableMultiSelectDropdown
                                    label="Education" icon="🎓"
                                    options={[...new Set(users.map(u => (u.course || u.education)?.trim()).filter(Boolean))]}
                                    selected={advancedFilters.educations} onChange={(val) => handleMultiSelect('educations', val)} onClear={() => handleClearMultiSelect('educations')}
                                    placeholder="Type or select education..."
                                />

                                <div className="au-advanced-section"><FaCalendarAlt /> Ranges</div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <input type="number" placeholder="Min Age" className="au-advanced-input" value={advancedFilters.ageMin} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, ageMin: e.target.value }))} style={{ width: '50%' }} />
                                    <input type="number" placeholder="Max Age" className="au-advanced-input" value={advancedFilters.ageMax} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, ageMax: e.target.value }))} style={{ width: '50%' }} />
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <input type="number" placeholder="Min Height (cm)" className="au-advanced-input" value={advancedFilters.heightMin} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, heightMin: e.target.value }))} style={{ width: '50%' }} />
                                    <input type="number" placeholder="Max Height (cm)" className="au-advanced-input" value={advancedFilters.heightMax} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, heightMax: e.target.value }))} style={{ width: '50%' }} />
                                </div>

                                <div className="au-advanced-section"><FaUtensils /> Lifestyle</div>
                                <SearchableMultiSelectDropdown
                                    label="Mother Tongue" icon="🗣️"
                                    options={[...new Set(users.map(u => u.mother_tongue?.trim()).filter(Boolean))]}
                                    selected={advancedFilters.motherTongues} onChange={(val) => handleMultiSelect('motherTongues', val)} onClear={() => handleClearMultiSelect('motherTongues')}
                                    placeholder="Type or select language..."
                                />
                                <SearchableMultiSelectDropdown
                                    label="Food Style" icon="🍽️"
                                    options={['Vegetarian', 'Non-Vegetarian', 'Eggetarian', 'Vegan', 'Jain']}
                                    selected={advancedFilters.foodStyles} onChange={(val) => handleMultiSelect('foodStyles', val)} onClear={() => handleClearMultiSelect('foodStyles')}
                                    placeholder="Type or select food style..."
                                />

                                <div>
                                    <label style={{ fontSize: 10.5, fontWeight: 700, color: '#c2185b', display: 'block', marginBottom: 4 }}>💍 MARITAL STATUS</label>
                                    <select className="au-advanced-input" value={advancedFilters.maritalStatus} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, maritalStatus: e.target.value }))}>
                                        <option value="all">All</option>
                                        <option value="Unmarried">Unmarried</option>
                                        <option value="Married">Married</option>
                                        <option value="Divorced">Divorced</option>
                                        <option value="Widowed">Widowed</option>
                                    </select>
                                </div>

                                <div className="au-advanced-section"><FaCheckCircle /> Filters</div>
                                <label className="au-advanced-checkbox"><input type="checkbox" checked={advancedFilters.showOnlyVerified} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, showOnlyVerified: e.target.checked }))} /> ✅ Verified Only</label>
                                <label className="au-advanced-checkbox"><input type="checkbox" checked={advancedFilters.showOnlyOnline} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, showOnlyOnline: e.target.checked }))} /> 🟢 Online Only</label>
                                <label className="au-advanced-checkbox"><input type="checkbox" checked={advancedFilters.showOnlyWithImages} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, showOnlyWithImages: e.target.checked }))} /> 📸 Photos Only</label>

                            </div>

                            <div className="au-advanced-actions">
                                <button className="au-apply-btn" onClick={() => setShowAdvancedSearch(false)}>Apply Filters</button>
                                <button className="au-clear-adv-btn" onClick={handleClearAllFilters}>Clear All</button>
                            </div>
                        </div>
                    )}

                    {activePlans.length === 0 && (
                        <div className="au-plan-banner" style={{ background: 'linear-gradient(135deg,#fff0f3,#ffe4ec)', border: '2px solid #ffd3df' }}>
                            <div style={{ fontSize: 28 }}>🔒</div>
                            <div className="au-plan-banner-text">
                                <p style={{ fontSize: 12, fontWeight: 800, color: '#c2185b', margin: 0 }}>Purchase a plan to send interest!</p>
                            </div>
                            <button onClick={() => navigate('/recharge')} style={{ padding: '8px 16px', background: 'linear-gradient(135deg,#ff0055,#ff4d88)', color: '#fff', border: 'none', borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Buy Plan</button>
                        </div>
                    )}

                    {filteredUsers.length > 0 ? (
                        <div className={`au-grid ${viewMode === 'grid' ? 'grid-mode' : 'list-mode'}`}>
                            {filteredUsers.map((user) => {
                                const userId = user.User_id || user.user_id || user.user || user.id || user.bid;
                                const isShortlisted = shortlistedIds.includes(String(userId));
                                const isInterest = interestSentIds.includes(String(userId));
                                const isLoad = actionLoading[userId];
                                const isBoosted = boostedIds.includes(String(userId));

                                return (
                                    <div key={userId} className={`au-card ${isBoosted ? 'boosted' : ''} ${viewMode === 'list' ? 'list-card' : ''}`}>
                                        {isBoosted && <div className="au-boost-tag">🚀 BOOSTED</div>}
                                        <div className="au-card-img-section">
                                            <div className="au-avatar-wrap">
                                                <img src={getDisplayImage(user)} alt={getDisplayName(user)} className="au-avatar" onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${getDisplayName(user)}&background=ff0055&color=fff&size=128`; }} />
                                                {user.is_online && <span className="au-online-dot" />}
                                            </div>
                                        </div>
                                        <div className="au-card-body">
                                            <div className="au-card-top-row">
                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                    <h3 className="au-name">{getDisplayName(user)}</h3>
                                                    <p className="au-id">ID: {getDisplayId(user)}</p>
                                                </div>
                                            </div>
                                            <div className="au-info-chips">
                                                {(user.age || user.height) && <span className="au-info-chip"><FaUser size={9} /> {user.age} Yrs {user.height ? `, ${user.height}` : ''}</span>}
                                                {getDisplayLocation(user) !== 'N/A' && <span className="au-info-chip"><FaMapMarkerAlt size={9} /> {getDisplayLocation(user)}</span>}
                                            </div>
                                            <div className="au-actions-row">
                                                <button className="au-action-btn au-btn-view" onClick={() => navigate(`/user-profile/${userId}`)}><FaEye size={10} /> View</button>
                                                <button className={`au-action-btn au-btn-interest ${isInterest ? 'sent' : !canSendInterest(userId) ? 'locked' : ''}`} onClick={() => handleInterest(userId)} disabled={isLoad === 'interest' || isInterest}>
                                                    {isLoad === 'interest' ? <FaSpinner size={10} style={{ animation: 'auSpin 1s linear infinite' }} /> : isInterest ? <>✅ Sent</> : !canSendInterest(userId) ? <><FaLock size={10} /> Locked</> : <><FaHeart size={10} /> Interest</>}
                                                </button>
                                                <button className={`au-action-btn au-btn-shortlist ${isShortlisted ? 'done' : ''}`} onClick={() => handleShortlist(userId)} disabled={isLoad === 'shortlist' || isShortlisted}>
                                                    {isLoad === 'shortlist' ? <FaSpinner size={10} style={{ animation: 'auSpin 1s linear infinite' }} /> : isShortlisted ? <>⭐ Saved</> : <><FaStar size={10} /> Save</>}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="au-empty">
                            <div className="au-empty-icon"><FaUser /></div>
                            <h3 className="au-empty-title">No Profiles Found</h3>
                            <button className="au-empty-btn" onClick={handleClearAllFilters}>Clear Filters</button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default AllUsers;